import { useState, useRef, useEffect, useMemo } from 'react'
import DatePicker, { DatePickerProps } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Calendar, Clock } from 'lucide-react'

interface DateRangePickerProps {
  checkIn: string
  checkOut: string
  nights: number
  errors: { [key: string]: string }
  onChange: (name: string, value: string) => void
  showHeading?: boolean
  isOpen?: boolean
  onClose?: () => void
}

const DateRangePicker = ({ 
  checkIn, 
  checkOut, 
  nights, 
  errors, 
  onChange, 
  showHeading = true,
  isOpen: externalIsOpen,
  onClose
}: DateRangePickerProps) => {
  const [startDate, setStartDate] = useState<Date | null>(checkIn ? new Date(checkIn) : null)
  const [endDate, setEndDate] = useState<Date | null>(checkOut ? new Date(checkOut) : null)
  const [isOpen, setIsOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  
  // Track months for consecutive display
  const [startMonth, setStartMonth] = useState<Date>(() => {
    if (checkIn) {
      const date = new Date(checkIn)
      date.setDate(1) // Set to first day of month
      return date
    }
    const today = new Date()
    today.setDate(1) // Set to first day of month
    return today
  })
  
  // Calculate end month (one month after start month)
  const endMonth = useMemo(() => {
    const nextMonth = new Date(startMonth)
    nextMonth.setMonth(nextMonth.getMonth() + 1)
    return nextMonth
  }, [startMonth])

  // Sync with external props
  useEffect(() => {
    if (checkIn) {
      const date = new Date(checkIn)
      setStartDate(date)
      setStartMonth(date)
    } else {
      setStartDate(null)
    }
  }, [checkIn])

  useEffect(() => {
    if (checkOut) {
      const date = new Date(checkOut)
      setEndDate(date)
    } else {
      setEndDate(null)
    }
  }, [checkOut])
  
  // Update start month when start date changes
  useEffect(() => {
    if (startDate) {
      setStartMonth(startDate)
    }
  }, [startDate])

  useEffect(() => {
    if (externalIsOpen !== undefined) {
      setIsOpen(externalIsOpen)
    }
  }, [externalIsOpen])

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date)
    if (date) {
      onChange('checkIn', date.toISOString().split('T')[0])
    } else {
      onChange('checkIn', '')
    }
    // Clear end date if start date is after end date
    if (date && endDate && date >= endDate) {
      setEndDate(null)
      onChange('checkOut', '')
    }
  }

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date)
    if (date) {
      onChange('checkOut', date.toISOString().split('T')[0])
      // Auto-close if both dates selected and external control
      if (externalIsOpen !== undefined && onClose && startDate) {
        setTimeout(() => onClose(), 300)
      }
    } else {
      onChange('checkOut', '')
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  // Calculate nights
  const calculatedNights = startDate && endDate
    ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0

  return (
    <div className="space-y-4 relative" ref={pickerRef}>
      {showHeading && (
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Select Your Dates</h3>
          <p className="text-xs sm:text-sm text-gray-600">Choose your check-in and check-out dates</p>
        </div>
      )}

      {/* Date Input Buttons - Only show if showHeading is true */}
      {showHeading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="block text-xs sm:text-sm font-semibold text-gray-800 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-primary-500" />
              Check In
            </label>
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              minDate={new Date()}
              dateFormat="MMM dd, yyyy"
              placeholderText="Select check-in date"
              className={`w-full px-3 py-3 border-2 rounded-xl text-left transition-all duration-200 hover:shadow-md ${
                errors.checkIn 
                  ? 'border-red-500 bg-red-50' 
                  : startDate
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              wrapperClassName="w-full"
              calendarStartDay={1}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              todayButton="Today"
              withPortal={false}
              portalId="root"
              popperPlacement="bottom-start"
              popperClassName="!z-[99999]"
              popperProps={{ strategy: 'fixed' }}
              fixedHeight
            />
            {errors.checkIn && (
              <p className="text-red-500 text-xs mt-1">{errors.checkIn}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs sm:text-sm font-semibold text-gray-800 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-primary-500" />
              Check Out
            </label>
            <DatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate || new Date()}
              dateFormat="MMM dd, yyyy"
              placeholderText={startDate ? 'Select check-out date' : 'Select check-in first'}
              disabled={!startDate}
              className={`w-full px-3 py-3 border-2 rounded-xl text-left transition-all duration-200 ${
                !startDate
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  : errors.checkOut 
                  ? 'border-red-500 bg-red-50 hover:shadow-md'
                  : endDate
                  ? 'border-primary-500 bg-primary-50 hover:shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
              wrapperClassName="w-full"
              calendarStartDay={1}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              todayButton="Today"
              withPortal={false}
              portalId="root"
              popperPlacement="bottom-start"
              popperClassName="!z-[99999]"
              popperProps={{ strategy: 'fixed' }}
              fixedHeight
            />
            {errors.checkOut && (
              <p className="text-red-500 text-xs mt-1">{errors.checkOut}</p>
            )}
          </div>
        </div>
      )}

      {/* Calendar Picker - Show when isOpen is true or when showHeading is false */}
      {(isOpen || !showHeading) && (
        <div className={`${showHeading ? 'absolute top-full left-0 mt-2 z-[9999]' : ''} bg-white rounded-xl shadow-2xl border border-gray-200 p-3 sm:p-4`}>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Start Date Calendar */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Check-in</label>
              <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                minDate={new Date()}
                inline
                calendarClassName="!border-0 !shadow-none"
                month={startMonth}
                onMonthChange={(date: Date) => {
                  setStartMonth(date)
                }}
                calendarStartDay={1}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                fixedHeight
                dateFormat="MMM dd, yyyy"
                renderCustomHeader={({
                  date,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => (
                  <div className="flex items-center justify-between px-2 py-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const newMonth = new Date(startMonth)
                        newMonth.setMonth(newMonth.getMonth() - 1)
                        setStartMonth(newMonth)
                      }}
                      disabled={prevMonthButtonDisabled}
                      className={`p-1 rounded hover:bg-gray-100 ${prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="text-sm font-semibold text-gray-700">
                      {startMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const newMonth = new Date(startMonth)
                        newMonth.setMonth(newMonth.getMonth() + 1)
                        setStartMonth(newMonth)
                      }}
                      disabled={nextMonthButtonDisabled}
                      className={`p-1 rounded hover:bg-gray-100 ${nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              />
            </div>

            {/* End Date Calendar */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Check-out</label>
              <DatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate || new Date()}
                inline
                disabled={!startDate}
                calendarClassName="!border-0 !shadow-none"
                month={endMonth}
                onMonthChange={(date: Date) => {
                  const newStartMonth = new Date(date)
                  newStartMonth.setMonth(newStartMonth.getMonth() - 1)
                  setStartMonth(newStartMonth)
                }}
                calendarStartDay={1}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                fixedHeight
                dateFormat="MMM dd, yyyy"
                renderCustomHeader={({
                  date,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => (
                  <div className="flex items-center justify-between px-2 py-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const newStartMonth = new Date(startMonth)
                        newStartMonth.setMonth(newStartMonth.getMonth() - 1)
                        setStartMonth(newStartMonth)
                      }}
                      disabled={prevMonthButtonDisabled}
                      className={`p-1 rounded hover:bg-gray-100 ${prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="text-sm font-semibold text-gray-700">
                      {endMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const newStartMonth = new Date(startMonth)
                        newStartMonth.setMonth(newStartMonth.getMonth() + 1)
                        setStartMonth(newStartMonth)
                      }}
                      disabled={nextMonthButtonDisabled}
                      className={`p-1 rounded hover:bg-gray-100 ${nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => {
                setStartDate(null)
                setEndDate(null)
                onChange('checkIn', '')
                onChange('checkOut', '')
              }}
              className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Clear dates
            </button>
            <button
              onClick={handleClose}
              className="px-3 py-1.5 text-xs sm:text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Stay Duration Display */}
      {startDate && endDate && calculatedNights > 0 && (
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Total Stay Duration</p>
              <p className="text-xl font-bold text-primary-700">
                {calculatedNights} night{calculatedNights !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DateRangePicker
